/* WizardStepper — indicador de progresso numerado para wizards multi-step.
   Padrão visual canônico do design system (ADR-0118, ADR-0119):
   círculos numerados (done=preenchido+check, active=anel primary, future=muted)
   com label e separador `—` entre passos. */
import { CheckIcon } from 'lucide-react';
import type { JSX } from 'react';

export interface WizardStepperItem {
  readonly id: string;
  readonly label: string;
}

export interface WizardStepperProps {
  readonly steps: ReadonlyArray<WizardStepperItem>;
  /** Índice do step atualmente ativo (base 0). */
  readonly currentIndex: number;
  readonly ariaLabel?: string | undefined;
}

export function WizardStepper({ steps, currentIndex, ariaLabel }: WizardStepperProps): JSX.Element {
  return (
    <ol className="flex flex-wrap items-center gap-2" aria-label={ariaLabel}>
      {steps.map((step, i) => {
        const isDone = i < currentIndex;
        const isActive = i === currentIndex;
        return (
          <li key={step.id} className="flex items-center gap-2">
            <span
              aria-current={isActive ? 'step' : undefined}
              className={
                isDone
                  ? 'bg-primary text-primary-foreground flex size-7 items-center justify-center rounded-full text-sm font-semibold'
                  : isActive
                    ? 'bg-primary/15 text-primary border-primary flex size-7 items-center justify-center rounded-full border-2 text-sm font-semibold'
                    : 'bg-muted text-muted-foreground border-border flex size-7 items-center justify-center rounded-full border text-sm font-semibold'
              }
            >
              {isDone ? <CheckIcon className="size-4" aria-hidden="true" /> : i + 1}
            </span>
            <span
              className={
                isActive
                  ? 'text-foreground text-sm font-semibold'
                  : isDone
                    ? 'text-foreground/80 text-sm font-medium'
                    : 'text-muted-foreground text-sm'
              }
            >
              {step.label}
            </span>
            {i < steps.length - 1 && (
              <span className="text-muted-foreground/40 ml-1" aria-hidden="true">
                —
              </span>
            )}
          </li>
        );
      })}
    </ol>
  );
}
